import { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"

import { Col, Row, Form, Label, Card, CardBody, Button } from "reactstrap"

// Custom Components
import Breadcrumbs from "../../../components/Common/Breadcrumb"

// Custom Hooks
import {
  useCreateVehicleStock,
  useEditVehicleStock,
  useVehicleStock,
} from "hooks/stockHook"

import InputField from "components/Common/InputField"
import { useToastMessage } from "hooks/useToastMessage"
import { modelToModelIdsNumber } from "utils/modelUtils"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import LabelButtons from "components/Common/LabelButtons"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"
import FullChassisNumber from "components/Common/FullChassisNumber"
import PublishDraftSwitch from "components/Common/PublishDraftSwitch/PublishDraftSwitch"
import FormikCreatableField from "components/Common/FormikCreatableField"
import { useListParams } from "hooks/useListParams"
import { useQueryClient } from "@tanstack/react-query"
import { VEHICLE_IMPORTER_STOCKS_QUERY_KEY } from "hooks/useVehicleImporter"

const StockAddEdit = () => {
  document.title = "Stock List"
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { id } = useParams()
  const { params: urlParams } = useListParams()
  const isEdit = !!id
  const importerId = urlParams?.importerId

  const { handleResponse, handleError } = useToastMessage()
  const [allFeatures, setAllFeatures] = useState([])
  const [allPlatforms, setAllPlatforms] = useState([])

  const { data: stockResponse, isLoading } = useVehicleStock(id)

  const [params, setParams] = useState({
    brand: "",
    model: "",
  })

  const handleParams = useCallback((name, value) => {
    setParams(prev => ({ ...prev, [name]: value }))
  }, [])

  // Memoize the params update to prevent unnecessary re-renders
  useEffect(() => {
    if (stockResponse?.data?.id) {
      setParams({
        brand: stockResponse?.data?.brand?.slug ?? "",
        model: stockResponse?.data?.model?.slug ?? "",
      })
    }
  }, [
    stockResponse?.data?.id,
    stockResponse?.data?.brand?.slug,
    stockResponse?.data?.model?.slug,
  ])

  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()
  const { mutateAsync: createStock, isPending: stockCreateLoading } =
    useCreateVehicleStock()

  const validationSchema = Yup.object({
    chassis_number: Yup.string(),
    engine_number: Yup.string(),
    asking_price: Yup.number().nullable(),
    discount_price: Yup.number().nullable(),
    mileage: Yup.number().nullable(),
    doors: Yup.number().nullable(),
    seats: Yup.number().nullable(),
    tire_size: Yup.string(),
    brta_reg_no: Yup.string(),
    remarks: Yup.string(),
    drive_link: Yup.string(),
    is_published: Yup.boolean(),
    vs_ready_stock: Yup.boolean(),
    vs_brand_id: Yup.string(),
    vs_model_id: Yup.string(),
    vs_location_id: Yup.string(),
    importer_id: Yup.string(),
    showroom_id: Yup.string(),
    vs_package_id: Yup.string(),
    vs_year_id: Yup.string(),
    vs_engine_cc_id: Yup.string(),
    vs_chassis_code_id: Yup.string(),
    vs_engine_code_id: Yup.string(),
    vs_fuel_type_id: Yup.string(),
    vs_engine_type_id: Yup.string(),
    vs_key_type_id: Yup.string(),
    vs_auction_grade_id: Yup.string(),
    vs_transmission_id: Yup.string(),
    vs_statuse_id: Yup.string(),
    vs_ex_color_id: Yup.string(),
    vs_int_color_id: Yup.string(),
    short_descriptions: Yup.string(),
    full_descriptions: Yup.string(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        featureIds: values.selectedFeatures?.map(n => Number(n)),
        platformIds: values.selectedPlatforms?.map(n => Number(n)),
        ...values,
      }
      const action = isEdit
        ? editStock({ id: stockResponse?.data?.id, ...payload })
        : createStock(payload)

      try {
        const res = await action

        handleResponse(
          res,
          `Stock ${isEdit ? "updated" : "created"} successfully.`
        )
        if (res.success && !isEdit) {
          resetForm()
        }
        // importerId is present in URL, navigate to importer profile stock tab, otherwise navigate to stock list
        if (importerId) {
          // invalidate the query for the importer stocks to ensure fresh data when navigating back to the importer profile
          queryClient.invalidateQueries({
            queryKey: [
              `${VEHICLE_IMPORTER_STOCKS_QUERY_KEY}-${importerId}`,
              "all",
            ],
          })

          navigate(`/importer-list/${importerId}/view?tab=stock&page=1`)
        } else {
          navigate("/stock-list")
        }
      } catch (error) {
        console.error(error)
        handleError(error, "Failed to save stock.")
      }
    },
    [
      isEdit,
      stockResponse?.data?.id,
      editStock,
      createStock,
      handleResponse,
      handleError,
      navigate,
    ]
  )

  // Memoize initial values to prevent unnecessary re-initialization
  const initialValues = useMemo(
    () => ({
      stock_id: stockResponse?.data?.stock_id ?? "",
      chassis_number: stockResponse?.data?.chassis_number ?? "",
      engine_number: stockResponse?.data?.engine_number ?? "",
      asking_price: stockResponse?.data?.asking_price ?? "",
      discount_price: stockResponse?.data?.discount_price ?? "",
      mileage: stockResponse?.data?.mileage ?? "",
      doors: stockResponse?.data?.doors ?? "4",
      seats: stockResponse?.data?.seats ?? "5",
      tire_size: stockResponse?.data?.tire_size ?? "",
      brta_reg_no: stockResponse?.data?.brta_reg_no ?? "",
      remarks: stockResponse?.data?.remarks ?? "",
      drive_link: stockResponse?.data?.drive_link ?? "",
      is_published: stockResponse?.data?.is_published ?? false,
      vs_ready_stock: stockResponse?.data?.vs_ready_stock ?? false,
      short_descriptions: stockResponse?.data?.short_descriptions ?? "",
      full_descriptions: stockResponse?.data?.full_descriptions ?? "",
      vs_brand_id: stockResponse?.data?.vs_brand_id ?? "",
      vs_model_id: stockResponse?.data?.vs_model_id ?? "",
      vs_location_id: stockResponse?.data?.vs_location_id ?? "",
      importer_id: stockResponse?.data?.importer_id ?? "",
      showroom_id: stockResponse?.data?.showroom_id ?? "",
      vs_package_id: stockResponse?.data?.vs_package_id ?? "",
      vs_year_id: stockResponse?.data?.vs_year_id ?? "",
      vs_engine_cc_id: stockResponse?.data?.vs_engine_cc_id ?? "",
      vs_chassis_code_id: stockResponse?.data?.vs_chassis_code_id ?? "",
      vs_engine_code_id: stockResponse?.data?.vs_engine_code_id ?? "",
      vs_fuel_type_id: stockResponse?.data?.vs_fuel_type_id ?? "",
      vs_engine_type_id: stockResponse?.data?.vs_engine_type_id ?? "",
      vs_key_type_id: stockResponse?.data?.vs_key_type_id ?? "",
      vs_auction_grade_id: stockResponse?.data?.vs_auction_grade_id ?? "",
      vs_transmission_id: stockResponse?.data?.vs_transmission_id ?? "",
      vs_statuse_id: stockResponse?.data?.vs_statuse_id ?? "",
      vs_ex_color_id: stockResponse?.data?.vs_ex_color_id ?? "",
      vs_int_color_id: stockResponse?.data?.vs_int_color_id ?? "",
      selectedFeatures:
        modelToModelIdsNumber(stockResponse?.data?.features) ?? [],
      selectedPlatforms:
        modelToModelIdsNumber(
          stockResponse?.data?.publishes?.map(publish => publish?.platform) ??
            []
        ) ?? [],
    }),
    [stockResponse?.data]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const {
    isLoading: optionLoading,
    brands: brandsData,
    models: modelsData,
    packages: packagesData,
    engineCodes: engineCodesData,
    chassisCodes: chassisCodesData,
    engineCCs: engineCcsData,
    features: featuresData,
    showrooms: showroomsData,
    platforms: platformsData,
    raw: rawData,
  } = useFilteredVehicleOptions(params)

  // Memoize all the derived data to prevent unnecessary re-renders
  const yearsData = useMemo(() => rawData?.years ?? [], [rawData?.years])
  const extColorsData = useMemo(
    () => rawData?.exterior_colors ?? [],
    [rawData?.exterior_colors]
  )
  const intColorsData = useMemo(
    () => rawData?.interior_colors ?? [],
    [rawData?.interior_colors]
  )
  const statusesData = useMemo(
    () => rawData?.statuses ?? [],
    [rawData?.statuses]
  )
  const auctionGradesData = useMemo(
    () => rawData?.auction_grades ?? [],
    [rawData?.auction_grades]
  )
  const transmissionsData = useMemo(
    () => rawData?.transmissions ?? [],
    [rawData?.transmissions]
  )
  const engineTypesData = useMemo(
    () => rawData?.engine_types ?? [],
    [rawData?.engine_types]
  )
  const fuelTypesData = useMemo(
    () => rawData?.fuel_types ?? [],
    [rawData?.fuel_types]
  )
  const keyTypesData = useMemo(
    () => rawData?.key_types ?? [],
    [rawData?.key_types]
  )
  const locationsData = useMemo(
    () => rawData?.locations ?? [],
    [rawData?.locations]
  )
  const importersData = useMemo(
    () => rawData?.importers ?? [],
    [rawData?.importers]
  )

  const matchedImporter = useMemo(() => {
    if (!importerId) return null
    return importersData.find(i => i.slug === importerId)
  }, [importerId, importersData])

  useEffect(() => {
    if (
      !isEdit &&
      matchedImporter &&
      formik.values.importer_id !== matchedImporter.id.toString()
    ) {
      formik.setFieldValue("importer_id", matchedImporter.id.toString())
    }
  }, [isEdit, matchedImporter])

  // Combine all features and model features data
  useEffect(() => {
    if (rawData?.features?.length > 0) {
      setAllFeatures(rawData.features)
    }
  }, [rawData?.features?.length])

  // Combine all platforms and model platforms data
  useEffect(() => {
    if (platformsData?.length > 0) {
      setAllPlatforms(rawData.social_platforms)
    }
  }, [platformsData?.length])

  // FIXED: Handle features for both create and edit modes
  const featuresInitialized = useRef(false)
  useEffect(() => {
    // Skip if features are still loading or already initialized
    if (optionLoading || featuresInitialized.current) return

    // For edit mode: Set features from API response when both data and features are available
    if (isEdit && stockResponse?.data?.features && allFeatures.length > 0) {
      const apiFeatureIds = modelToModelIdsNumber(stockResponse.data.features)

      // Only update if we have actual features from API and they're different from current
      if (
        apiFeatureIds.length > 0 &&
        JSON.stringify(apiFeatureIds) !==
          JSON.stringify(formik.values.selectedFeatures)
      ) {
        formik.setFieldValue("selectedFeatures", apiFeatureIds)
        featuresInitialized.current = true
      }
    }
    // For create mode: Set features from model when model changes
    else if (!isEdit && featuresData && featuresData.length > 0) {
      const modelFeatureIds = modelToModelIdsNumber(featuresData)
      if (
        JSON.stringify(modelFeatureIds) !==
        JSON.stringify(formik.values.selectedFeatures)
      ) {
        formik.setFieldValue("selectedFeatures", modelFeatureIds)
        featuresInitialized.current = true
      }
    }
  }, [
    isEdit,
    featuresData,
    stockResponse?.data?.features,
    allFeatures.length,
    optionLoading,
    formik.values.selectedFeatures,
  ])

  // FIXED: Handle platforms for both create and edit modes
  const platformsInitialized = useRef(false)
  useEffect(() => {
    // Skip if platforms are still loading or already initialized
    if (optionLoading || platformsInitialized.current) return

    // For edit mode: Set platforms from API response when both data and platforms are available
    if (isEdit && stockResponse?.data?.platforms && allPlatforms.length > 0) {
      const apiPlatformIds = modelToModelIdsNumber(stockResponse.data.platforms)

      // Only update if we have actual platforms from API and they're different from current
      if (
        apiPlatformIds.length > 0 &&
        JSON.stringify(apiPlatformIds) !==
          JSON.stringify(formik.values.selectedPlatforms)
      ) {
        formik.setFieldValue("selectedPlatforms", apiPlatformIds)
        platformsInitialized.current = true
      }
    }
    // For create mode: Set platforms from model when model changes
    // else if (!isEdit && platformsData && platformsData.length > 0) {
    //   const modelPlatformsIds = modelToModelIdsNumber(platformsData)
    //   if (
    //     JSON.stringify(modelPlatformsIds) !==
    //     JSON.stringify(formik.values.selectedPlatforms)
    //   ) {
    //     formik.setFieldValue("selectedPlatforms", modelPlatformsIds)
    //     platformsInitialized.current = true
    //   }
    // }
  }, [
    isEdit,
    stockResponse?.data?.platforms,
    allPlatforms.length,
    optionLoading,
    formik.values.selectedPlatforms,
  ])

  // FIXED: Reset featuresInitialized when brand/model changes in create mode
  useEffect(() => {
    if (!isEdit) {
      featuresInitialized.current = false
    }
  }, [isEdit, formik.values.vs_brand_id, formik.values.vs_model_id])

  const featuresBtnData = useMemo(
    () => [
      {
        type: "add",
        btnId: "add-feature",
        btnName: "Add feature",
        onClick: () => navigate("/feature-list"),
      },
    ],
    [navigate]
  )

  // Reset model and model-associated fields when brand changes
  const previousBrandId = useRef(formik.values.vs_brand_id)
  useEffect(() => {
    if (
      formik.values.vs_brand_id &&
      formik.values.vs_brand_id !== previousBrandId.current
    ) {
      previousBrandId.current = formik.values.vs_brand_id

      formik.setValues(
        prev => ({
          ...prev,
          vs_model_id: "",
          vs_package_id: "",
          vs_engine_cc_id: "",
          vs_chassis_code_id: "",
          vs_engine_code_id: "",
          selectedFeatures: [],
          selectedPlatforms: [],
        }),
        false
      )

      // Reset features initialization when brand changes
      featuresInitialized.current = false
    }
  }, [formik.values.vs_brand_id])

  // Reset model-associated fields when model changes
  const previousModelId = useRef(formik.values.vs_model_id)
  useEffect(() => {
    if (
      formik.values.vs_model_id &&
      formik.values.vs_model_id !== previousModelId.current
    ) {
      previousModelId.current = formik.values.vs_model_id

      formik.setValues(
        prev => ({
          ...prev,
          vs_package_id: "",
          vs_engine_cc_id: "",
          vs_chassis_code_id: "",
          vs_engine_code_id: "",
          // For create mode, features will be set by the features effect
          // For edit mode, we keep the existing features
          selectedFeatures: isEdit ? prev.selectedFeatures : [],
          selectedPlatforms: isEdit ? prev.selectedPlatforms : [],
        }),
        false
      )

      // Reset features initialization when model changes in create mode
      if (!isEdit) {
        featuresInitialized.current = false
      }
    }
  }, [formik.values.vs_model_id, featuresData, isEdit])

  // FIXED: Enhanced auto-select logic that works for both create and edit modes
  const didInit = useRef(false)
  const hasSetDefaults = useRef(false)

  useEffect(() => {
    // Skip if still loading or already initialized
    if (optionLoading || isLoading || didInit.current) return

    const timeout = setTimeout(() => {
      // For edit mode, we should respect existing values from API
      // For create mode, we set defaults

      // Common fields that should have defaults in create mode
      if (!isEdit) {
        if (keyTypesData?.length > 0 && !formik.values.vs_key_type_id) {
          formik.setFieldValue("vs_key_type_id", keyTypesData[0].id.toString())
        }
        if (
          transmissionsData?.length > 0 &&
          !formik.values.vs_transmission_id
        ) {
          formik.setFieldValue(
            "vs_transmission_id",
            transmissionsData[0].id.toString()
          )
        }
        if (showroomsData?.length > 0 && !formik.values.showroom_id) {
          formik.setFieldValue("showroom_id", showroomsData[0].id.toString())
        }
        if (statusesData?.length > 0 && !formik.values.vs_statuse_id) {
          formik.setFieldValue("vs_statuse_id", statusesData[0].id.toString())
        }
        if (fuelTypesData?.length > 0 && !formik.values.vs_fuel_type_id) {
          formik.setFieldValue(
            "vs_fuel_type_id",
            fuelTypesData[0].id.toString()
          )
        }
        if (engineTypesData?.length > 0 && !formik.values.vs_engine_type_id) {
          formik.setFieldValue(
            "vs_engine_type_id",
            engineTypesData[0].id.toString()
          )
        }
      }

      // Model-dependent fields - only set defaults if no value is present and options are available
      const setDefaultIfEmpty = (fieldName, options) => {
        if (options?.length > 0 && !formik.values[fieldName]) {
          formik.setFieldValue(fieldName, options[0].id.toString())
        }
      }

      setDefaultIfEmpty("vs_chassis_code_id", chassisCodesData)
      setDefaultIfEmpty("vs_package_id", packagesData)
      setDefaultIfEmpty("vs_engine_cc_id", engineCcsData)
      setDefaultIfEmpty("vs_engine_code_id", engineCodesData)

      didInit.current = true
      hasSetDefaults.current = true
    }, 100)

    return () => clearTimeout(timeout)
  }, [
    keyTypesData,
    transmissionsData,
    showroomsData,
    statusesData,
    fuelTypesData,
    engineTypesData,
    chassisCodesData,
    packagesData,
    engineCcsData,
    engineCodesData,
    formik.values,
    optionLoading,
    isLoading,
    isEdit,
  ])

  // FIXED: Additional effect to handle edit mode specifically
  useEffect(() => {
    if (
      !isEdit ||
      !stockResponse?.data ||
      optionLoading ||
      !hasSetDefaults.current
    )
      return

    // Only set values if they're empty and options are available
    const setEditValueIfEmpty = (fieldName, options, apiValue) => {
      if (options?.length > 0 && !formik.values[fieldName] && apiValue) {
        formik.setFieldValue(fieldName, apiValue.toString())
      }
    }

    setEditValueIfEmpty(
      "vs_chassis_code_id",
      chassisCodesData,
      stockResponse.data.vs_chassis_code_id
    )
    setEditValueIfEmpty(
      "vs_package_id",
      packagesData,
      stockResponse.data.vs_package_id
    )
    setEditValueIfEmpty(
      "vs_engine_cc_id",
      engineCcsData,
      stockResponse.data.vs_engine_cc_id
    )
    setEditValueIfEmpty(
      "vs_engine_code_id",
      engineCodesData,
      stockResponse.data.vs_engine_code_id
    )
  }, [
    isEdit,
    stockResponse?.data,
    chassisCodesData,
    packagesData,
    engineCcsData,
    engineCodesData,
    optionLoading,
    formik.values,
  ])

  // Memoize the button text to prevent unnecessary re-renders
  const buttonText = useMemo(() => {
    const loading = stockEditLoading || stockCreateLoading
    return `${isEdit ? "Save Changes" : "Add Stock"}${loading ? "..." : ""}`
  }, [isEdit, stockEditLoading, stockCreateLoading])

  // Memoize the disabled state for the submit button
  const isSubmitDisabled = useMemo(
    () => optionLoading || isLoading || stockEditLoading || stockCreateLoading,
    [optionLoading, isLoading, stockEditLoading, stockCreateLoading]
  )

  // Handle back navigation
  const handleBack = () => {
    if (importerId) {
      navigate(`/importer-list/${importerId}/view?tab=stock&page=1`)
    } else {
      navigate("/stock-list")
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Stock" breadcrumbItem="Stock List" />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <Row className="border-bottom d-flex justify-content-between align-items-center pb-3">
                  <Col xs={12} sm={6}>
                    <h5 className="mb-0">Stock List</h5>
                  </Col>
                  <Col xs={12} sm={6} className="text-sm-end mt-2 mt-sm-0">
                    <div>
                      <Button
                        color="primary"
                        onClick={() => handleBack()}
                        className="me-1"
                      >
                        Back to List
                      </Button>
                      <Button color="danger" onClick={() => formik.resetForm()}>
                        Reset Form
                      </Button>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardBody>
                <Row className="mb-3">
                  <Col>
                    <Form onSubmit={formik.handleSubmit}>
                      {/* Vehicle Identification Group */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Vehicle Identification</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_brand_id"
                                label="Brand"
                                formik={formik}
                                data={brandsData}
                                onChange={(value, selectedBrand) => {
                                  handleParams(
                                    "brand",
                                    selectedBrand?.slug ?? ""
                                  )
                                }}
                                placeholder="Select Brand"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_model_id"
                                label="Model"
                                formik={formik}
                                data={modelsData}
                                onChange={(value, selectedModel) => {
                                  handleParams(
                                    "model",
                                    selectedModel?.slug ?? ""
                                  )
                                }}
                                placeholder="Select Model"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_year_id"
                                label="Year"
                                formik={formik}
                                data={yearsData}
                                placeholder="Select Year"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_engine_type_id"
                                label="Engine Type"
                                formik={formik}
                                data={engineTypesData}
                                placeholder="Select Engine Type"
                              />
                            </Col>

                            <Col sm={12} md={8} lg={6} xl={4}>
                              <FullChassisNumber
                                chassisCodesData={chassisCodesData}
                                formik={formik}
                                optionLoading={optionLoading}
                                isLoading={isLoading}
                                id={id}
                                isEdit={isEdit}
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_package_id"
                                label="Package"
                                formik={formik}
                                data={packagesData}
                                placeholder="Select Package"
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Vehicle Specifications Group */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Vehicle Specifications</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_auction_grade_id"
                                label="Auction Grade"
                                formik={formik}
                                data={auctionGradesData}
                                placeholder="Select Grade"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="mileage">Mileage</Label>
                              <InputField
                                type="number"
                                name="mileage"
                                value={formik.values.mileage}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_key_type_id"
                                label="Key Type"
                                formik={formik}
                                data={keyTypesData}
                                placeholder="Select Key Type"
                              />
                            </Col>
                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_engine_cc_id"
                                label="Engine CC"
                                formik={formik}
                                data={engineCcsData}
                                placeholder="Select Engine CC"
                              />
                            </Col>

                            <Col sm={12} md={8} lg={6} xl={4}>
                              <Row>
                                <Col xs={12} sm={5} className="mb-3">
                                  <FormikCreatableField
                                    name="vs_engine_code_id"
                                    label="Engine Code"
                                    formik={formik}
                                    data={engineCodesData}
                                    placeholder="Select Code"
                                  />
                                </Col>

                                <Col xs={12} sm={7} className="mb-3">
                                  <Label htmlFor="engine_number">
                                    Engine Number
                                  </Label>
                                  <InputField
                                    type="text"
                                    name="engine_number"
                                    value={formik.values.engine_number}
                                    disabled={!formik.values.vs_engine_code_id}
                                    onChange={formik.handleChange}
                                    loading={optionLoading || isLoading}
                                  />
                                </Col>
                              </Row>
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_fuel_type_id"
                                label="Fuel Type"
                                formik={formik}
                                data={fuelTypesData}
                                placeholder="Select Fuel Type"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_transmission_id"
                                label="Transmission"
                                formik={formik}
                                data={transmissionsData}
                                placeholder="Select Transmission"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="doors">Doors</Label>
                              <InputField
                                type="number"
                                name="doors"
                                value={formik.values.doors}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="seats">Seats</Label>
                              <InputField
                                type="number"
                                name="seats"
                                value={formik.values.seats}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="tire_size">Tire Size</Label>
                              <InputField
                                type="text"
                                name="tire_size"
                                value={formik.values.tire_size}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Vehicle Appearance Group */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Vehicle Appearance</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_ex_color_id"
                                label="Exterior Color"
                                formik={formik}
                                data={extColorsData}
                                placeholder="Select Color"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_int_color_id"
                                label="Interior Color"
                                formik={formik}
                                data={intColorsData}
                                placeholder="Select Color"
                              />
                            </Col>

                            <Col sm={12} md={8} lg={9} xl={10} className="mb-3">
                              <LabelButtons
                                name="Features"
                                buttonsData={featuresBtnData}
                              />
                              <ButtonCheckboxList
                                items={allFeatures}
                                name="selectedFeatures"
                                itemName="features"
                                selected={formik.values.selectedFeatures}
                                onChange={newSelected =>
                                  formik.setFieldValue(
                                    "selectedFeatures",
                                    newSelected
                                  )
                                }
                                loading={optionLoading || isLoading}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Vehicle Condition & History Group */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Vehicle Registration Details</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="brta_reg_no">BRTA Reg No</Label>
                              <InputField
                                type="text"
                                name="brta_reg_no"
                                value={formik.values.brta_reg_no}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Inventory & Pricing Group */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Inventory & Pricing</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            {!importerId && (
                              <CanView
                                access={[
                                  ACCESS.STOCK.CREATE_IMPORTER,
                                  ACCESS.STOCK.EDIT_IMPORTER,
                                ]}
                              >
                                <Col
                                  sm={12}
                                  md={4}
                                  lg={3}
                                  xl={2}
                                  className="mb-3"
                                >
                                  <FormikCreatableField
                                    name="importer_id"
                                    label="Importer"
                                    formik={formik}
                                    data={importersData}
                                    placeholder="Select Importer"
                                  />
                                </Col>
                              </CanView>
                            )}

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_location_id"
                                label="Location"
                                formik={formik}
                                data={locationsData}
                                placeholder="Select Location"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="showroom_id"
                                label="Showroom"
                                formik={formik}
                                data={
                                  showroomsData?.map(showroom => ({
                                    ...showroom,
                                    name: showroom?.label ?? "",
                                  })) ?? []
                                }
                                placeholder="Select Showroom"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <FormikCreatableField
                                name="vs_statuse_id"
                                label="Status"
                                formik={formik}
                                data={statusesData}
                                placeholder="Select Status"
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="asking_price">Asking Price</Label>
                              <InputField
                                type="number"
                                name="asking_price"
                                value={formik.values.asking_price}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>

                            <Col sm={12} md={4} lg={3} xl={2} className="mb-3">
                              <Label htmlFor="discount_price">
                                Discount Price (Last Price)
                              </Label>
                              <InputField
                                type="number"
                                name="discount_price"
                                value={formik.values.discount_price}
                                onChange={formik.handleChange}
                                loading={isLoading}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Description Group */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Vehicle Description</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} className="mb-3">
                              <Label htmlFor="short_descriptions">
                                Short Description (For Listing)
                              </Label>
                              <InputField
                                type="textarea"
                                name="short_descriptions"
                                value={formik.values.short_descriptions}
                                onChange={formik.handleChange}
                              />
                            </Col>

                            <Col sm={12} className="mb-3">
                              <Label htmlFor="full_descriptions">
                                Full Description (Detailed)
                              </Label>
                              <InputField
                                type="textarea"
                                name="full_descriptions"
                                value={formik.values.full_descriptions}
                                onChange={formik.handleChange}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Vehicle Publishment */}
                      <CanView access={[ACCESS.STOCK.VIEW_STOCK_PUBLISHMENT]}>
                        <div className="card mb-4">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">Vehicle Publishment</h6>
                          </div>
                          <div className="card-body">
                            <Row>
                              <CanView
                                access={[
                                  ACCESS.STOCK
                                    .CREATE_STOCK_PUBLISHABLE_PLATFORM,
                                  ACCESS.STOCK.EDIT_STOCK_PUBLISHABLE_PLATFORM,
                                ]}
                              >
                                <Col
                                  sm={12}
                                  md={8}
                                  lg={9}
                                  xl={10}
                                  className="mb-3"
                                >
                                  <LabelButtons name="Publishable Platform" />
                                  <ButtonCheckboxList
                                    items={allPlatforms}
                                    selected={formik.values.selectedPlatforms}
                                    onChange={newSelected =>
                                      formik.setFieldValue(
                                        "selectedPlatforms",
                                        newSelected
                                      )
                                    }
                                  />
                                </Col>
                              </CanView>
                            </Row>
                          </div>
                        </div>
                      </CanView>
                      {/* Remarks */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Reference & Comments</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} className="mb-3">
                              <Label htmlFor="remarks">Remarks</Label>
                              <InputField
                                type="text"
                                name="remarks"
                                value={formik.values.remarks}
                                onChange={formik.handleChange}
                                loading={optionLoading || isLoading}
                              />
                            </Col>
                            <Col sm={12} md={4} className="mb-3">
                              <Label htmlFor="drive_link">Drive Link</Label>
                              <InputField
                                type="text"
                                name="drive_link"
                                value={formik.values.drive_link}
                                onChange={formik.handleChange}
                                loading={optionLoading || isLoading}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>
                      {/* Draft & Publish */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Draft & Publish</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} className="mb-3">
                              <div>
                                <PublishDraftSwitch
                                  initialValue={formik.values.is_published}
                                  onToggle={newStatus => {
                                    formik.setFieldValue(
                                      "is_published",
                                      newStatus
                                    )
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Ready Stock */}
                      <div className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Ready Stock</h6>
                        </div>
                        <div className="card-body">
                          <Row>
                            <Col sm={12} md={4} className="mb-3">
                              <div>
                                <PublishDraftSwitch
                                  initialValue={formik.values.vs_ready_stock}
                                  trueLabel="Ready Stock"
                                  falseLabel="Not Ready"
                                  trueIcon="bx-check-circle"
                                  falseIcon="bx-time-five"
                                  onToggle={newStatus => {
                                    formik.setFieldValue(
                                      "vs_ready_stock",
                                      newStatus
                                    )
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>

                      <div className="text-end">
                        <Button
                          type="submit"
                          color="success"
                          disabled={
                            isSubmitDisabled ||
                            (formik.touched.chassis_number &&
                              formik.errors.chassis_number)
                          }
                          id="stock-add-edit-btn"
                        >
                           <i className={`bx bx-save`} /> {buttonText}
                        </Button>
                      </div>
                      {formik.touched.chassis_number &&
                        formik.errors.chassis_number && (
                          <div className="text-end text-danger italic">
                            <small>Chassis number already exists!</small>
                          </div>
                        )}
                    </Form>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default StockAddEdit
